import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { isoToEU } from "../../lib/format";
import { useSafeFetch } from "../../lib/useSafeFetch";
import { useConfirm } from "../../components/ConfirmDialog";
import DatePicker from "../../components/DatePicker";
import { Pencil, Trash2, Plus, X, Tag, Eye } from "lucide-react";

const EMPTY_FORM = {
  code: "",
  type: "percent",
  value: 10,
  min_subtotal: 0,
  max_discount: "",
  valid_from: "",
  valid_until: "",
  valid_type: "booking_period",
  discount_scope: "total",
  is_automatic: false,
  max_uses: 0,
  applicable_categories: [],
  description: "",
  active: true,
};

const CATEGORIES = ["luxury", "sports", "suv", "electric", "economy"];

export default function AdminVouchers() {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [previewVoucher, setPreviewVoucher] = useState(null);
  const [previewBase, setPreviewBase] = useState(500);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const { data: items, loading, refetch: load } = useSafeFetch(
    "/admin/vouchers", { defaultValue: [], asList: true },
  );
  const { confirm, dialog: confirmDialog } = useConfirm();

  const openCreate = () => { setEditing("new"); setForm(EMPTY_FORM); setError(""); };
  const openEdit = (v) => {
    setEditing(v.id);
    setForm({
      code: v.code || "",
      type: v.type || "percent",
      value: v.value ?? 0,
      min_subtotal: v.min_subtotal ?? 0,
      max_discount: v.max_discount ?? "",
      valid_from: v.valid_from ? v.valid_from.slice(0, 10) : "",
      valid_until: v.valid_until ? v.valid_until.slice(0, 10) : "",
      valid_type: v.valid_type || "booking_period",
      discount_scope: v.discount_scope || "total",
      is_automatic: v.is_automatic === true,
      max_uses: v.max_uses ?? 0,
      applicable_categories: v.applicable_categories || [],
      description: v.description || "",
      active: v.active !== false,
    });
    setError("");
  };
  const close = () => { setEditing(null); setError(""); };

  const submitForm = async (e) => {
    e.preventDefault();
    setError("");
    // Date pickers give plain YYYY-MM-DD; expand into an ISO datetime at
    // midnight UTC so the backend keeps its existing datetime storage
    // contract without accidentally shifting a day due to TZ math.
    const toIsoStart = (d) => (d ? `${d}T00:00:00Z` : null);
    const toIsoEnd   = (d) => (d ? `${d}T23:59:59Z` : null);
    const payload = {
      code: form.code.trim().toUpperCase(),
      type: form.type,
      value: Number(form.value) || 0,
      min_subtotal: Number(form.min_subtotal) || 0,
      max_discount: form.max_discount === "" ? null : Number(form.max_discount),
      valid_from: toIsoStart(form.valid_from),
      valid_until: toIsoEnd(form.valid_until),
      valid_type: form.valid_type || "booking_period",
      discount_scope: form.discount_scope || "total",
      is_automatic: form.is_automatic === true,
      max_uses: parseInt(form.max_uses, 10) || 0,
      applicable_categories: form.applicable_categories,
      description: form.description || "",
      active: form.active !== false,
    };
    try {
      if (editing === "new") await api.post("/admin/vouchers", payload);
      else await api.put(`/admin/vouchers/${editing}`, payload);
      await load();
      close();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const handleDelete = async (id, code) => {
    const ok = await confirm({
      title:        "Delete voucher",
      message:      "This action is permanent and cannot be undone. Historical bookings that used this code keep it as text; only new redemptions are blocked.",
      subject:      code,
      confirmLabel: "Delete voucher",
      testId:       "confirm-delete-voucher",
    });
    if (!ok) return;
    await api.delete(`/admin/vouchers/${id}`);
    load();
  };

  const toggleActive = async (v) => {
    const payload = {
      code: v.code,
      type: v.type,
      value: v.value,
      min_subtotal: v.min_subtotal || 0,
      max_discount: v.max_discount,
      valid_from: v.valid_from,
      valid_until: v.valid_until,
      max_uses: v.max_uses || 0,
      applicable_categories: v.applicable_categories || [],
      description: v.description || "",
      active: !v.active,
    };
    await api.put(`/admin/vouchers/${v.id}`, payload);
    load();
  };

  const openPreview = (v) => {
    setPreviewVoucher(v);
    setPreviewResult(null);
    setPreviewBase(500);
  };

  const runPreview = useCallback(async () => {
    if (!previewVoucher) return;
    setPreviewLoading(true);
    try {
      const { data } = await api.get(
        `/admin/vouchers/${previewVoucher.id}/preview`,
        { params: { base_amount: Number(previewBase) || 0 } },
      );
      setPreviewResult(data);
    } catch (err) {
      setPreviewResult({ error: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setPreviewLoading(false);
    }
  }, [previewVoucher, previewBase]);

  useEffect(() => {
    if (previewVoucher) runPreview();
  }, [previewVoucher, runPreview]);

  const toggleCategory = (cat) => {
    setForm((f) => ({
      ...f,
      applicable_categories: f.applicable_categories.includes(cat)
        ? f.applicable_categories.filter((c) => c !== cat)
        : [...f.applicable_categories, cat],
    }));
  };

  return (
    <div data-testid="admin-vouchers-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Promotions</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            Voucher <em className="italic">codes</em>.
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-xl">
            Create percentage or flat-rate promo codes. Validations (min subtotal, vehicle categories, usage limits, validity windows) are enforced at booking time.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="bg-black text-white dark:bg-white dark:text-black px-5 py-2.5 text-sm hover:opacity-90 transition-opacity inline-flex items-center gap-2 rounded"
          data-testid="add-voucher"
        >
          <Plus size={14} /> New voucher
        </button>
      </div>

      <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400 border-b border-black/8 dark:border-white/10 bg-[#FAF9F6] dark:bg-[#111827]">
            <tr>
              <th className="py-3 px-4">Code</th>
              <th className="py-3 px-4">Type</th>
              <th className="py-3 px-4">Value</th>
              <th className="py-3 px-4">Min spend</th>
              <th className="py-3 px-4">Used / max</th>
              <th className="py-3 px-4">Window</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={8} className="py-8 text-center text-neutral-500">Loading…</td></tr>)}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="py-10 text-center text-neutral-500">No vouchers yet — create your first promo code.</td></tr>
            )}
            {!loading && items.map((v) => (
              <tr key={v.id} className="border-b border-black/5 dark:border-white/5" data-testid={`voucher-row-${v.code}`}>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <Tag size={13} className="text-neutral-400" />
                    <span className="font-mono font-medium">{v.code}</span>
                  </div>
                  {v.description && (
                    <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">{v.description}</p>
                  )}
                </td>
                <td className="py-3 px-4 capitalize">{v.type}</td>
                <td className="py-3 px-4 tabular-nums">
                  {v.type === "percent" ? `${v.value}%` : `€${Number(v.value).toFixed(2)}`}
                  {v.type === "percent" && v.max_discount != null && (
                    <span className="text-[11px] text-neutral-500 ml-1">(cap €{Number(v.max_discount).toFixed(0)})</span>
                  )}
                </td>
                <td className="py-3 px-4 tabular-nums">€{Number(v.min_subtotal || 0).toFixed(0)}</td>
                <td className="py-3 px-4 tabular-nums">
                  {v.current_uses ?? 0} / {v.max_uses > 0 ? v.max_uses : "∞"}
                </td>
                <td className="py-3 px-4 text-[11px] text-neutral-500 dark:text-neutral-400">
                  {v.valid_from ? isoToEU(v.valid_from.slice(0, 10)) : "—"}
                  <br />→ {v.valid_until ? isoToEU(v.valid_until.slice(0, 10)) : "no end"}
                </td>
                <td className="py-3 px-4">
                  <button
                    onClick={() => toggleActive(v)}
                    className={`text-xs px-2 py-0.5 rounded ${v.active ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"}`}
                    data-testid={`toggle-${v.code}`}
                  >
                    {v.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="py-3 px-4 text-right whitespace-nowrap">
                  <button onClick={() => openPreview(v)} className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10 rounded" data-testid={`preview-${v.code}`} title="Preview discount math"><Eye size={14} /></button>
                  <button onClick={() => openEdit(v)} className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10 rounded ml-1" data-testid={`edit-${v.code}`}><Pencil size={14} /></button>
                  <button onClick={() => handleDelete(v.id, v.code)} className="p-1.5 hover:bg-red-50 dark:hover:bg-rose-900/30 text-red-600 dark:text-rose-400 rounded ml-1" data-testid={`delete-${v.code}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={close}>
          <div
            className="bg-white dark:bg-[#1F2937] text-[#0A0A0A] dark:text-[#F9FAFB] max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto rounded border border-black/10 dark:border-white/10"
            onClick={(e) => e.stopPropagation()}
            data-testid="voucher-form-modal"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-3xl">{editing === "new" ? "New voucher" : `Edit ${form.code}`}</h2>
              <button onClick={close} aria-label="Close"><X /></button>
            </div>

            <form onSubmit={submitForm} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                <Field label="Code (uppercased)">
                  <Input
                    required
                    maxLength={32}
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    className="font-mono"
                    data-testid="v-code"
                  />
                </Field>
                <Field label="Type">
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-2 outline-none focus:border-black dark:focus:border-white text-sm"
                    data-testid="v-type"
                  >
                    <option value="percent">Percent off</option>
                    <option value="fixed">Fixed amount off (€)</option>
                  </select>
                </Field>
                <Field label={form.type === "percent" ? "Value (%)" : "Value (€)"}>
                  <Input
                    required
                    type="number"
                    step="0.01"
                    min={0}
                    max={form.type === "percent" ? 100 : undefined}
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                    data-testid="v-value"
                  />
                </Field>
                <Field label="Min subtotal (€)">
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.min_subtotal}
                    onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })}
                    data-testid="v-min-subtotal"
                  />
                </Field>
                {form.type === "percent" && (
                  <Field label="Max discount cap (€) — optional">
                    <Input
                      type="number"
                      step="0.01"
                      min={0}
                      value={form.max_discount}
                      onChange={(e) => setForm({ ...form, max_discount: e.target.value })}
                      placeholder="No cap"
                      data-testid="v-max-discount"
                    />
                  </Field>
                )}
                <Field label="Max uses (0 = unlimited)">
                  <Input
                    type="number"
                    step="1"
                    min={0}
                    value={form.max_uses}
                    onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                    data-testid="v-max-uses"
                  />
                </Field>
                <Field label="Valid from (optional)">
                  <DatePicker
                    value={form.valid_from}
                    onChange={(v) => setForm({ ...form, valid_from: v || "" })}
                    testId="v-valid-from"
                  />
                </Field>
                <Field label="Valid until (optional)">
                  <DatePicker
                    value={form.valid_until}
                    onChange={(v) => setForm({ ...form, valid_until: v || "" })}
                    testId="v-valid-until"
                  />
                </Field>
                <Field label="Active?">
                  <div className="pt-2">
                    <Toggle checked={form.active !== false} onChange={(val) => setForm({ ...form, active: val })} testId="v-active-toggle" />
                  </div>
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Validity type">
                  <Select
                    value={form.valid_type}
                    onChange={(e) => setForm({ ...form, valid_type: e.target.value })}
                    testId="v-valid-type"
                  >
                    <option value="booking_period">Booking Period (created within window)</option>
                    <option value="rental_period">Rental Period (travel within window)</option>
                  </Select>
                </Field>
                <Field label="Discount scope">
                  <Select
                    value={form.discount_scope}
                    onChange={(e) => setForm({ ...form, discount_scope: e.target.value })}
                    testId="v-discount-scope"
                  >
                    <option value="total">Total Reservation Price</option>
                    <option value="base_only">Base Rental Fee Only</option>
                  </Select>
                </Field>
                <Field label="Automatic?">
                  <div className="pt-2">
                    <Toggle
                      checked={form.is_automatic === true}
                      onChange={(val) => setForm({ ...form, is_automatic: val })}
                      testId="v-is-automatic-toggle"
                    />
                    <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1 leading-snug">
                      When on, applies automatically if dates qualify (customer doesn't need to type the code).
                    </p>
                  </div>
                </Field>
              </div>

              <Field label="Description (shown to customers)">
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="e.g. Summer 2026 promo"
                  data-testid="v-description"
                />
              </Field>

              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 mb-3">
                  Applicable categories (empty = all)
                </p>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((cat) => {
                    const on = form.applicable_categories.includes(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => toggleCategory(cat)}
                        className={`text-xs px-3 py-1.5 rounded border capitalize transition-colors ${on
                          ? "bg-black text-white border-black dark:bg-white dark:text-black dark:border-white"
                          : "border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white text-neutral-700 dark:text-neutral-300"}`}
                        data-testid={`v-cat-${cat}`}
                      >
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>

              {error && <p className="text-red-600 dark:text-rose-400 text-sm" data-testid="v-form-error">{error}</p>}

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={close} className="border border-black/15 dark:border-white/15 px-6 py-2.5 text-sm hover:border-black dark:hover:border-white transition-colors rounded">Cancel</button>
                <button type="submit" className="bg-black text-white dark:bg-white dark:text-black px-6 py-2.5 text-sm hover:opacity-90 transition-opacity rounded" data-testid="v-submit">
                  {editing === "new" ? "Create voucher" : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview drawer */}
      {previewVoucher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setPreviewVoucher(null)}>
          <div
            className="bg-white dark:bg-[#1F2937] text-[#0A0A0A] dark:text-[#F9FAFB] max-w-lg w-full p-8 rounded border border-black/10 dark:border-white/10"
            onClick={(e) => e.stopPropagation()}
            data-testid="voucher-preview-modal"
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Discount preview</p>
                <h2 className="font-display text-3xl mt-1 font-mono">{previewVoucher.code}</h2>
              </div>
              <button onClick={() => setPreviewVoucher(null)} aria-label="Close"><X /></button>
            </div>

            <Field label="Hypothetical base amount (€)">
              <Input
                type="number"
                step="1"
                min={0}
                value={previewBase}
                onChange={(e) => setPreviewBase(e.target.value)}
                onBlur={runPreview}
                data-testid="preview-base-input"
              />
            </Field>

            <div className="mt-6 border border-black/8 dark:border-white/10 rounded divide-y divide-black/5 dark:divide-white/5 text-sm">
              <PreviewRow label="Voucher type" value={previewVoucher.type} mono />
              <PreviewRow label="Value" value={previewVoucher.type === "percent" ? `${previewVoucher.value}%` : `${Number(previewVoucher.value).toFixed(2)}`} />
              {previewLoading && (
                <div className="px-4 py-3 text-neutral-500 italic">Calculating…</div>
              )}
              {!previewLoading && previewResult && !previewResult.error && (
                <>
                  <PreviewRow label="Base" value={`${Number(previewResult.base).toFixed(2)}`} />
                  <PreviewRow
                    label="Discount"
                    value={`–${Number(previewResult.discount).toFixed(2)}`}
                    highlight="emerald"
                    testId="preview-discount"
                  />
                  <PreviewRow
                    label="Final amount"
                    value={`${Number(previewResult.final).toFixed(2)}`}
                    bold
                    testId="preview-final"
                  />
                </>
              )}
              {!previewLoading && previewResult?.error && (
                <div className="px-4 py-3 text-red-600 dark:text-rose-400 text-sm">{previewResult.error}</div>
              )}
            </div>

            <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-4 leading-relaxed">
              This preview shows the raw discount math only. Live bookings additionally enforce min subtotal, usage limits, validity windows and category restrictions.
            </p>

            <div className="flex justify-end pt-6">
              <button onClick={() => setPreviewVoucher(null)} className="border border-black/15 dark:border-white/15 px-6 py-2.5 text-sm hover:border-black dark:hover:border-white transition-colors rounded">Done</button>
            </div>
          </div>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 block mb-2">{label}</span>
      {children}
    </label>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full bg-transparent border-b border-black/15 dark:border-white/15 py-2 outline-none focus:border-black dark:focus:border-white transition-colors text-sm ${className}`}
    />
  );
}

function Select({ className = "", testId, children, ...props }) {
  return (
    <select
      {...props}
      data-testid={testId}
      className={`w-full bg-transparent border-b border-black/15 dark:border-white/15 py-2 outline-none focus:border-black dark:focus:border-white transition-colors text-sm ${className}`}
    >
      {children}
    </select>
  );
}

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-emerald-600 dark:bg-emerald-500" : "bg-neutral-300 dark:bg-neutral-700"}`}
      data-testid={testId}
      aria-pressed={checked}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0"}`} />
    </button>
  );
}

function PreviewRow({ label, value, bold, mono, highlight, testId }) {
  const colour = highlight === "emerald"
    ? "text-emerald-700 dark:text-emerald-400"
    : bold ? "" : "text-neutral-700 dark:text-neutral-300";
  return (
    <div className="flex justify-between items-baseline px-4 py-2.5">
      <span className="text-[11px] tracking-[0.18em] uppercase text-neutral-500 dark:text-neutral-400">{label}</span>
      <span
        className={`tabular-nums ${mono ? "font-mono" : ""} ${bold ? "font-display text-2xl" : "text-sm"} ${colour}`}
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}
